/**
 * Vue du jour du planning : liste TOUTES les tâches d'un jour (la case du calendrier n'en montre
 * que `maxPerDay`), navigation jour précédent / suivant, et création d'une tâche à cette date.
 * `onCreate` absent (rôle sans droit d'écriture) : pas de bouton de création.
 * L'appartenance d'une tâche à un jour reste décidée par le parent (`tasksForDay`).
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Empty, StatusTag, taskReference } from './common.jsx';
import { Modal } from './Modal.jsx';
import { addDays } from '../lib/calendar.js';
import { BTN_GHOST, BTN_GHOST_SM } from '../ui/theme.js';

export function DayTasksModal({
  day,
  locale,
  projectKey,
  tasksForDay,
  onShiftDay,
  onOpenTask,
  onCreate,
  onClose,
}) {
  const { t } = useTranslation();
  const dayLabel = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      }),
    [locale],
  );
  const tasks = tasksForDay(day);
  const title = dayLabel.format(new Date(`${day}T00:00:00Z`));

  return (
    <Modal title={title} onClose={onClose} size="lg">
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex gap-2">
            <button
              type="button"
              className={BTN_GHOST_SM}
              aria-label={t('planning.day.prevAria')}
              onClick={() => onShiftDay(addDays(day, -1))}
            >
              ‹
            </button>
            <button
              type="button"
              className={BTN_GHOST_SM}
              aria-label={t('planning.day.nextAria')}
              onClick={() => onShiftDay(addDays(day, 1))}
            >
              ›
            </button>
          </div>
          <span className="font-mono text-[11px] text-ink-faint">
            {t('planning.day.count', { count: tasks.length })}
          </span>
        </div>

        {tasks.length === 0 ? (
          <Empty>{t('planning.day.empty')}</Empty>
        ) : (
          <ul className="flex max-h-[60vh] flex-col divide-y-2 divide-line overflow-y-auto border-2 border-line">
            {tasks.map((task) => (
              <li key={task.id}>
                <button
                  type="button"
                  className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 bg-paper px-3 py-2 text-left hover:bg-surface"
                  onClick={() => onOpenTask(task.id)}
                >
                  <span className="font-mono text-[11px] text-ink-faint">
                    {task.kind === 'milestone' ? '◆ ' : ''}
                    {taskReference(projectKey, task.number)}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm text-ink">{task.title}</span>
                  <span className="font-mono text-[11px] text-ink-faint">
                    {task.kind === 'milestone' || task.startDate === task.dueDate
                      ? (task.dueDate ?? task.startDate)
                      : `${task.startDate} → ${task.dueDate}`}
                  </span>
                  <StatusTag status={task.status} />
                  <span className="text-xs text-ink-soft">
                    {task.assignee?.name ?? t('taskDrawer.noAssignee')}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" className={BTN_GHOST} onClick={onClose}>
            {t('planning.day.close')}
          </button>
          {onCreate && (
            <button type="button" className={BTN_GHOST} onClick={() => onCreate(day)}>
              {t('planning.day.newTask')}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
